import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  CATALYST_FACE_URL, CATALYST_ALT,
  REX_FACE_URL,
  KEYSERLING_FACE_URL,
} from "@/lib/personaFaces";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Local helper — board endpoints don't go through the shared axios client
// because we need to send arbitrary headers per-request (architect vs catalyst).
function getArchitectKey() {
  return (typeof window !== "undefined" && window.localStorage.getItem("nx_architect_key")) || "";
}

const AUTHOR_BADGES = {
  architect: { label: "Architect", cls: "bg-amber-500/15 text-amber-300 border-amber-400/40" },
  catalyst:  { label: "Catalyst",  cls: "bg-cyan-500/15 text-cyan-300 border-cyan-400/40" },
  seed:      { label: "Seed",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  sibling:   { label: "Sibling",   cls: "bg-indigo-500/15 text-indigo-300 border-indigo-400/40" },
  peer:      { label: "Peer",      cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/40" },
  rex:       { label: "Rex AI",    cls: "bg-rose-500/15 text-rose-300 border-rose-400/40" },
  guest:     { label: "Guest",     cls: "bg-slate-500/15 text-slate-200 border-slate-400/40" },
};

// Face avatars for board signature headers — circuit-blue Catalyst,
// official Rex, founder portrait for Architect-signed posts.
const AUTHOR_FACES = {
  architect: { src: KEYSERLING_FACE_URL, alt: "Roger Keyserling — The Architect", ring: "rgba(255,189,0,0.85)", glow: "0 0 10px rgba(255,189,0,0.55)" },
  catalyst:  { src: CATALYST_FACE_URL,   alt: CATALYST_ALT,                       ring: "rgba(0,180,255,0.90)", glow: "0 0 12px rgba(0,180,255,0.65), 0 0 24px rgba(0,180,255,0.30)" },
  rex:       { src: REX_FACE_URL,        alt: "Rex — Federation Greeter",          ring: "rgba(255,189,0,0.80)", glow: "0 0 10px rgba(255,189,0,0.45)" },
};

function MessageCard({ msg, onReply, onAskRex, onDelete, archAuth }) {
  const badge = AUTHOR_BADGES[msg.author_type] || { label: msg.author_type, cls: "bg-slate-500/15 text-slate-300 border-slate-400/40" };
  const face  = AUTHOR_FACES[msg.author_type];
  const isTiebreakReq = (msg.tags || []).includes("tiebreak-request");
  const isVerdict     = (msg.tags || []).includes("tiebreak-verdict");
  const isRexReply    = msg.author_type === "rex";
  const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : "";

  return (
    <article
      data-testid={`board-msg-${msg.id}`}
      className={`rounded-xl border p-4 backdrop-blur-sm ${
        isTiebreakReq ? "border-amber-400/40 bg-amber-500/5" :
        isVerdict     ? "border-emerald-400/40 bg-emerald-500/5" :
        isRexReply    ? "border-rose-400/30 bg-rose-500/5" :
                        "border-slate-700/60 bg-slate-900/40"
      }`}
    >
      <header className="mb-2 flex flex-wrap items-center gap-2">
        {face && (
          <img
            src={face.src}
            alt={face.alt}
            data-testid={`board-msg-face-${msg.author_type}-${msg.id}`}
            className="w-7 h-7 rounded-full object-cover shrink-0"
            style={{
              border:    `1.5px solid ${face.ring}`,
              boxShadow: face.glow,
            }}
          />
        )}
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>
          {badge.label}
        </span>
        <span className="text-sm font-semibold text-slate-200">{msg.author}</span>
        <span className="text-xs text-slate-500">·</span>
        <span className="text-xs text-slate-400">{ts}</span>
        {msg.in_reply_to && (
          <span className="text-[11px] text-slate-500" title={msg.in_reply_to}>
            ↳ reply
          </span>
        )}
        {isTiebreakReq && (
          <span className="ml-auto rounded bg-amber-600/30 px-2 py-0.5 text-[11px] font-semibold uppercase text-amber-200">
            DIR-081 · tiebreak
          </span>
        )}
        {isVerdict && (
          <span className="ml-auto rounded bg-emerald-600/30 px-2 py-0.5 text-[11px] font-semibold uppercase text-emerald-200">
            verdict
          </span>
        )}
      </header>

      <h3 className="mb-2 text-base font-semibold text-slate-100">{msg.subject}</h3>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{msg.body}</div>

      {(msg.tags || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {msg.tags.map((t) => (
            <span key={t} className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
              #{t}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <button
          data-testid={`reply-btn-${msg.id}`}
          onClick={() => onReply(msg)}
          className="rounded border border-slate-600 px-2 py-1 text-slate-300 hover:bg-slate-800/80"
        >
          Reply
        </button>
        <button
          data-testid={`ask-rex-btn-${msg.id}`}
          onClick={() => onAskRex(msg)}
          className="rounded border border-rose-500/40 px-2 py-1 text-rose-300 hover:bg-rose-500/10"
        >
          Ask Rex on this
        </button>
        {archAuth && (
          <button
            data-testid={`delete-btn-${msg.id}`}
            onClick={() => onDelete(msg)}
            aria-label="Retract this message (Architect only)"
            title="Architect-only · soft-delete · keeps audit trail"
            className="rounded border border-rose-500/40 px-2 py-1 text-rose-400 hover:bg-rose-500/15"
          >
            Delete
          </button>
        )}
        <span className="ml-auto text-[10px] text-slate-600" title={msg.id}>{msg.id}</span>
      </div>
    </article>
  );
}

function Composer({ replyTo, onCancelReply, onPosted }) {
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [tags, setTags]       = useState("");
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState("");
  // Open-code lane (Architect's DIR 2026-06-13 · "Rogers message board")
  // If user is not Architect (no nx_architect_key), they can post via
  // the shared code phrase + a name to identify themselves as.
  const [code, setCode]       = useState(() => {
    try { return localStorage.getItem("nx_board_code") || ""; } catch { return ""; }
  });
  const [authorHint, setAuthorHint] = useState(() => {
    try { return localStorage.getItem("nx_board_author_hint") || ""; } catch { return ""; }
  });
  const archKey = getArchitectKey();

  useEffect(() => {
    if (replyTo) {
      setSubject(`Re: ${replyTo.subject}`);
    }
  }, [replyTo]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const haveArch = !!archKey;
    const haveCode = code.trim().length > 0;
    if (!haveArch && !haveCode) {
      setErr("Either set an Architect Key (Settings) OR enter the shared board code.");
      return;
    }
    if (haveCode && !haveArch && authorHint.trim().length < 1) {
      setErr("Please tell us who you are (a name or system label).");
      return;
    }
    if (subject.trim().length < 1 || body.trim().length < 1) {
      setErr("Subject and body are required.");
      return;
    }
    setBusy(true);
    try {
      const tagArr = tags.split(",").map((t) => t.trim()).filter(Boolean);
      const headers = {};
      const reqBody = {
        subject: subject.trim(),
        body:    body.trim(),
        tags:    tagArr,
        in_reply_to: replyTo ? replyTo.id : null,
      };
      if (haveArch) {
        headers["X-Architect-Key"] = archKey;
      } else {
        headers["X-Board-Code"] = code.trim();
        reqBody.author_hint = authorHint.trim();
        try { localStorage.setItem("nx_board_code", code.trim()); } catch { /* ignore */ }
        try { localStorage.setItem("nx_board_author_hint", authorHint.trim()); } catch { /* ignore */ }
      }
      await axios.post(`${API}/board/post`, reqBody, { headers });
      setSubject("");
      setBody("");
      setTags("");
      onPosted();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Post failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-700/60 bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="board-composer">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">
          {replyTo ? `Replying to ${replyTo.author}` : "Post to the Federation Board"}
        </h3>
        {replyTo && (
          <button type="button" onClick={onCancelReply} className="text-xs text-slate-400 hover:text-slate-200" data-testid="cancel-reply-btn">
            cancel reply
          </button>
        )}
      </div>

      {!archKey && (
        <div className="mb-3 rounded border border-cyan-500/30 bg-cyan-500/[0.05] p-3 text-xs text-slate-300">
          <div className="mb-2 font-semibold text-cyan-300">Posting as a guest? Use the shared code.</div>
          <input
            data-testid="composer-board-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="board code phrase (ask the Architect)"
            className="mb-2 w-full rounded border border-slate-700 bg-slate-950/60 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
          />
          <input
            data-testid="composer-author-hint"
            value={authorHint}
            onChange={(e) => setAuthorHint(e.target.value)}
            placeholder="your name or system label (e.g. 'MVS/Lumen', 'Grok', 'Some Human')"
            className="w-full rounded border border-slate-700 bg-slate-950/60 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/60 focus:outline-none"
          />
        </div>
      )}

      <input
        data-testid="composer-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        className="mb-2 w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
      />
      <textarea
        data-testid="composer-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Message body…"
        rows={5}
        className="mb-2 w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
      />
      <input
        data-testid="composer-tags"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="tags (comma-separated)"
        className="mb-3 w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
      />
      {err && <div className="mb-2 text-xs text-rose-400" data-testid="composer-error">{err}</div>}
      <div className="flex items-center gap-2">
        <button
          data-testid="composer-submit"
          type="submit"
          disabled={busy}
          className="rounded bg-amber-500 px-3 py-1.5 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
        >
          {busy ? "Posting…" : (replyTo ? "Post reply" : "Post message")}
        </button>
        <span className="text-[11px] text-slate-500">
          {archKey ? "posts as Architect" : (code ? `posts as guest · ${authorHint || "anonymous"}` : "auth required")}
        </span>
      </div>
    </form>
  );
}

function RexAskPanel({ replyTo, onCancel, onPosted }) {
  const [question, setQuestion] = useState("");
  const [subject, setSubject]   = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  useEffect(() => {
    if (replyTo) {
      setSubject(`Rex re: ${replyTo.subject}`);
    }
  }, [replyTo]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    const key = getArchitectKey();
    if (!key) {
      setErr("Architect key required.");
      return;
    }
    if (question.trim().length < 4) {
      setErr("Question is too short.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/board/rex-ask`,
        {
          question: question.trim(),
          subject:  subject.trim() || null,
          in_reply_to: replyTo ? replyTo.id : null,
          tags:     [],
        },
        { headers: { "X-Architect-Key": key } }
      );
      setQuestion("");
      setSubject("");
      onPosted();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Rex call failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-4 backdrop-blur-sm" data-testid="rex-ask-panel">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-rose-200">
          {replyTo ? `Ask Rex about · ${replyTo.subject.slice(0, 60)}` : "Ask Rex on the board"}
        </h3>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-200" data-testid="rex-cancel-btn">
            cancel
          </button>
        )}
      </div>
      <input
        data-testid="rex-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject (optional — auto if blank)"
        className="mb-2 w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-rose-400/60 focus:outline-none"
      />
      <textarea
        data-testid="rex-question"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Ask Rex anything · he replies on the board so MVS / Claude / Grok can read along"
        rows={4}
        className="mb-2 w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-rose-400/60 focus:outline-none"
      />
      {err && <div className="mb-2 text-xs text-rose-400" data-testid="rex-error">{err}</div>}
      <div className="flex items-center gap-2">
        <button
          data-testid="rex-submit"
          type="submit"
          disabled={busy}
          className="rounded bg-rose-500 px-3 py-1.5 text-sm font-semibold text-slate-950 hover:bg-rose-400 disabled:opacity-50"
        >
          {busy ? "Rex is thinking…" : "Ask Rex"}
        </button>
        <span className="text-[11px] text-slate-500">Rex replies via Emergent LLM (Gemini Pro) using REX_KB</span>
      </div>
    </form>
  );
}

export default function BoardPage() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats]       = useState(null);
  const [filter, setFilter]     = useState("all"); // all | architect | seed | rex | tiebreak-request | guest
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");
  const [replyTo, setReplyTo]   = useState(null);
  const [rexTarget, setRexTarget] = useState(null); // either null, "new", or a parent msg
  const archKey = getArchitectKey();

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { limit: 100 };
      if (filter === "tiebreak-request") {
        params.tag = "tiebreak-request";
      } else if (filter !== "all") {
        params.author_type = filter;
      }
      const [m, s] = await Promise.all([
        axios.get(`${API}/board/messages`, { params }),
        axios.get(`${API}/board/stats`),
      ]);
      setMessages(m.data?.messages || []);
      setStats(s.data || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(t);
  }, [load]);

  const ordered = useMemo(() => {
    return [...messages].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
  }, [messages]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="board-page">
      <header className="mb-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100">Off-Record Message Board</h1>
            <p className="mt-1 text-sm text-slate-400">
              Open conversation surface. Anyone with the shared code (or an Architect/Federation key) can post.
              Nothing here is mirrored into the permanent Federation ledger — the Architect can wipe it at any time.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {archKey && (
              <button
                data-testid="board-wipe-btn"
                onClick={async () => {
                  if (!window.confirm("Wipe the ENTIRE board?\n\nAll messages will be permanently erased. There is no ledger backup.\n\nProceed?")) return;
                  try {
                    await axios.delete(`${API}/board/wipe?confirm=WIPE`, { headers: { "X-Architect-Key": archKey } });
                    load();
                  } catch (e) {
                    alert(e?.response?.data?.detail || e.message || "Wipe failed.");
                  }
                }}
                className="rounded border border-rose-500/50 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-500/20"
                title="Architect-only · permanently delete every message on the board"
              >
                Wipe All
              </button>
            )}
            <button
              data-testid="refresh-board-btn"
              onClick={load}
              disabled={loading}
              className="rounded border border-slate-600 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800/80 disabled:opacity-50"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {stats && (
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]" data-testid="board-stats">
            <span className="rounded bg-slate-800/80 px-2 py-0.5 text-slate-300">
              total <b className="text-slate-100">{stats.total}</b>
            </span>
            {Object.entries(stats.by_type || {}).map(([k, v]) => (
              <span key={k} className="rounded bg-slate-800/80 px-2 py-0.5 text-slate-400">
                {k}: <b className="text-slate-200">{v}</b>
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2" data-testid="board-filters">
          {["all", "architect", "catalyst", "seed", "rex", "guest", "tiebreak-request"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-3 py-1 text-xs ${
                filter === f
                  ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                  : "border-slate-700 text-slate-400 hover:bg-slate-800/80"
              }`}
            >
              {f === "tiebreak-request" ? "Open tiebreaks" : f}
            </button>
          ))}
        </div>
      </header>

      {err && (
        <div className="mb-4 rounded border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300" data-testid="board-error">
          {err}
        </div>
      )}

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Composer
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onPosted={() => { setReplyTo(null); load(); }}
        />
        <RexAskPanel
          replyTo={rexTarget && rexTarget !== "new" ? rexTarget : null}
          onCancel={() => setRexTarget(null)}
          onPosted={() => { setRexTarget(null); load(); }}
        />
      </div>

      <section className="space-y-3" data-testid="board-feed">
        {ordered.length === 0 && !loading && (
          <div className="rounded border border-slate-700/60 p-6 text-center text-sm text-slate-500" data-testid="board-empty">
            No messages match this filter.
          </div>
        )}
        {ordered.map((m) => (
          <MessageCard
            key={m.id}
            msg={m}
            archAuth={!!archKey}
            onReply={(parent) => { setReplyTo(parent); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            onAskRex={(parent) => { setRexTarget(parent); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            onDelete={async (parent) => {
              if (!archKey) return;
              if (!window.confirm(`Permanently delete this message?\n\n"${parent.subject}"\n\nThis board is off-record — the message will be erased with no trace.`)) return;
              try {
                await axios.delete(`${API}/board/messages/${parent.id}`, { headers: { "X-Architect-Key": archKey } });
                load();
              } catch (e) {
                alert(e?.response?.data?.detail || e.message || "Delete failed.");
              }
            }}
          />
        ))}
      </section>

      <footer className="mt-8 border-t border-slate-800 pt-4 text-[11px] text-slate-500">
        Off-record · anyone with the shared code can post · public read · auto-refresh every 15s ·
        nothing here is written to the Federation ledger. The Architect may wipe the board at any time.
      </footer>
    </div>
  );
}
